import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { type WebViewIntent } from '@/lib/react-native/services/react-native.intent'
import { useId, useMemo, type ReactElement } from 'react'
import { toast } from 'react-toastify' // eslint-disable-line no-restricted-imports
import { Button } from '../buttons/button/Button.client'
import { Icon } from '../icons/icon/Icon'
import { createNativeToastIntent } from './toast.intents'

export type ToastProps = UiComponentProps<{
  title?: string
  message?: string | null
  onClose?: () => void
}> &
  (
    | {
        actionTitle: string
        actionIcon?: ReactElement<{ className?: string }>
        onAction: () => void
      }
    | {
        actionTitle?: never
        actionIcon?: never
        onAction?: never
      }
  )

type ToastCallOptions = ToastProps & {
  /** Per-call override of the hook-level `dedupe`. */
  dedupe?: boolean
  actionIntent?: WebViewIntent
}

type UseToastOptions = {
  /** When `true` and no `id` is passed at call time, each variant gets its own slot id so the
   * same variant deduplicates. When `false`, no toastId is set: every call stacks a new toast. */
  dedupe?: boolean
}

function ToastBody({
  title,
  message,
  actionTitle,
  actionIcon,
  onAction,
  id,
  'data-testid': dataTestId,
  className,
}: ToastProps) {
  return (
    <div className={twMerge('flex flex-1 flex-col', className)} id={id} data-testid={dataTestId}>
      <div className="line-clamp-3 font-bold">{title}</div>
      {message && (
        <div className="mt-0.5 line-clamp-6 leading-light text-brand-2-900">{message}</div>
      )}
      {actionTitle && onAction && id && (
        <Button
          iconRight={actionIcon}
          variant="link"
          className="mt-0.5 w-fit self-start !text-brand-2-900"
          onPress={() => {
            onAction()
            toast.dismiss(id)
          }}
        >
          {actionTitle}
        </Button>
      )}
    </div>
  )
}

function success(props: ToastProps) {
  return toast.success(<ToastBody {...props} />, {
    toastId: props.id,
    className: '!bg-success-bg text-success-fg',
    progressClassName: '!bg-success-fg',
    icon: <Icon.CircleCheck size="md" className="text-success-fg" />,
    onClose: props.onClose,
  })
}

function info(props: ToastProps) {
  return toast.info(<ToastBody {...props} />, {
    toastId: props.id,
    className: '!bg-info-bg text-info-fg',
    progressClassName: '!bg-info-fg',
    icon: <Icon.Info size="md" className="text-info-fg" />,
    onClose: props.onClose,
  })
}

function warning(props: ToastProps) {
  return toast.warning(<ToastBody {...props} />, {
    toastId: props.id,
    className: '!bg-warning-bg text-warning-fg',
    progressClassName: '!bg-warning-fg',
    icon: <Icon.TriangleAlert size="md" className="text-warning-fg" />,
    onClose: props.onClose,
  })
}

function error(props: ToastProps) {
  return toast.error(<ToastBody {...props} />, {
    toastId: props.id,
    className: '!bg-error-bg text-error-fg',
    progressClassName: '!bg-error-fg',
    icon: <Icon.TriangleAlert size="md" className="text-error-fg" />,
    onClose: props.onClose,
  })
}

type ToastVariant = 'success' | 'info' | 'warning' | 'error'

export function useToast({ dedupe: hookDedupe = true }: UseToastOptions = {}) {
  const { t } = useTranslation(['Toast'])
  const instanceId = useId()

  const slotIds = useMemo(
    () =>
      ({
        success: `${instanceId}-success`,
        info: `${instanceId}-info`,
        warning: `${instanceId}-warning`,
        error: `${instanceId}-error`,
      }) satisfies Record<ToastVariant, string>,
    [instanceId],
  )

  const defaultCopy = useMemo(
    () => ({
      success: {
        title: t('Toast:success.default.title'),
        message: t('Toast:success.default.message'),
      },
      error: {
        title: t('Toast:error.default.title'),
        message: t('Toast:error.default.message'),
      },
    }),
    [t],
  )

  const isInWebView = useIsReactNativeWebView()

  return useMemo(
    () => ({
      success: ({ dedupe = hookDedupe, actionIntent, ...props }: ToastCallOptions = {}) => {
        const merged: ToastProps = {
          ...defaultCopy.success,
          ...props,
          id: props.id ?? (dedupe ? slotIds.success : undefined),
        }

        if (isInWebView) {
          return handleIntent(
            createNativeToastIntent('success', instanceId, {
              title: merged.title,
              message: merged.message,
              actionTitle: merged.actionTitle,
              actionIntent,
            }),
          )
        }

        return success(merged)
      },
      info: ({ dedupe = hookDedupe, ...props }: ToastCallOptions = {}) => {
        const merged: ToastProps = {
          ...props,
          id: props.id ?? (dedupe ? slotIds.info : undefined),
        }

        if (isInWebView) {
          return handleIntent(
            createNativeToastIntent('info', instanceId, {
              title: merged.title,
              message: merged.message,
            }),
          )
        }

        return info(merged)
      },
      warning: ({ dedupe = hookDedupe, ...props }: ToastCallOptions = {}) => {
        const merged: ToastProps = {
          ...props,
          id: props.id ?? (dedupe ? slotIds.warning : undefined),
        }

        if (isInWebView) {
          return handleIntent(
            createNativeToastIntent('warning', instanceId, {
              title: merged.title,
              message: merged.message,
            }),
          )
        }

        return warning(merged)
      },
      error: ({ dedupe = hookDedupe, ...props }: ToastCallOptions = {}) => {
        const merged: ToastProps = {
          ...defaultCopy.error,
          ...props,
          id: props.id ?? (dedupe ? slotIds.error : undefined),
        }

        if (isInWebView) {
          return handleIntent(
            createNativeToastIntent('error', instanceId, {
              title: merged.title,
              message: merged.message,
            }),
          )
        }

        return error(merged)
      },
    }),
    [defaultCopy, hookDedupe, instanceId, slotIds, isInWebView],
  )
}
